import { useMemo } from "react";
import { BsFilter } from "react-icons/bs";
import { CourseLevel } from "@shared_types";
import CycleButton, { CycleOption } from "./CycleButton";
import { getLevelColorByDifficulty } from "@/styles/courseStyles";

interface LevelCycleButtonProps {
  selectedLevel: number | null;
  levelsData?: { levels: CourseLevel[] };
  levelsLoading: boolean;
  /** Emits the new level's `difficulty` (or null while loading). */
  onLevelChange: (level: number | null) => void;
}

/**
 * In-place level cycle pill (PWA port of client-native LevelCycleButton).
 * Replaces the old FiltersDrawer Chakra modal. Tap = next level (loops),
 * long-press / right-click = previous. Background = the level's color by
 * difficulty. Emits `difficulty` (NOT id) to match iOS filtering.
 */
export default function LevelCycleButton({
  selectedLevel,
  levelsData,
  levelsLoading,
  onLevelChange,
}: LevelCycleButtonProps) {
  const levelOptions = useMemo((): CycleOption<number | null>[] => {
    if (levelsLoading || !levelsData?.levels) {
      return [
        { label: "Loading...", value: null, color: "var(--color-grey-other)" },
      ];
    }
    return levelsData.levels.map((level) => ({
      label: level.name,
      value: level.difficulty,
      color: getLevelColorByDifficulty(level.difficulty),
    }));
  }, [levelsData, levelsLoading]);

  const currentIndex = useMemo(() => {
    if (selectedLevel === null || selectedLevel === 0) return 0;
    const idx = levelOptions.findIndex(
      (option) => option.value === selectedLevel
    );
    return idx >= 0 ? idx : 0;
  }, [selectedLevel, levelOptions]);

  if (levelsLoading) {
    return (
      <CycleButton
        options={[
          { label: "Loading...", value: null, color: "var(--color-grey-other)" },
        ]}
        index={0}
        loop={false}
        haptics={false}
        icon={<BsFilter color="#fff" size={16} />}
      />
    );
  }

  return (
    <CycleButton
      options={levelOptions}
      index={currentIndex}
      loop
      haptics
      icon={<BsFilter color="#fff" size={16} />}
      onChange={(option) => onLevelChange(option.value)}
    />
  );
}
